import { useEffect, useState, type RefObject } from 'react'

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

/**
 * Atrapa el foco dentro de `containerRef` mientras un diálogo modal está
 * abierto (CLAUDE.md §6, fase 6: "navegable por teclado entero"): lo mueve
 * dentro al montar (si nada dentro lo tiene ya, p. ej. por `autoFocus`),
 * Tab/Shift+Tab no se escapan al resto de la página, `Esc` cierra, y al
 * desmontar se lo devuelve a quien lo tenía antes de abrirse.
 */
export function useFocusTrap(
  containerRef: RefObject<HTMLElement | null>,
  onClose: () => void,
): void {
  // Capturado con el inicializador perezoso de `useState`, que corre en la
  // fase de render — antes de que se confirme el DOM del propio diálogo y de
  // que un `autoFocus` suyo se lo robe. Capturarlo dentro del efecto (que
  // corre después de esa confirmación) llegaría tarde.
  const [previouslyFocused] = useState<HTMLElement | null>(
    () => document.activeElement as HTMLElement | null,
  )

  useEffect(() => {
    const container = containerRef.current

    if (container && !container.contains(document.activeElement)) {
      const first = container.querySelector<HTMLElement>(FOCUSABLE_SELECTOR)
      ;(first ?? container).focus()
    }

    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        onClose()
        return
      }
      if (event.key !== 'Tab' || !container) return

      const focusable = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (!first || !last) return

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      previouslyFocused?.focus()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose])
}
